// ─────────────────────────────────────────────────────────────────────────────
// Session hook — who is signed in, and what may they do.
//
// `can()` is imported from the SAME shared module the server uses, so the UI can
// never disagree with the server about what a role means. The UI hides what the
// server would refuse; the server remains the enforcement point.
// ─────────────────────────────────────────────────────────────────────────────

import { can, type ErpRole, type Permission } from "@shared/rbac";
import { trpc, type RouterOutputs } from "../api";

type Me = RouterOutputs["auth"]["me"];
export type Actor = Extract<Me, { authenticated: true }>["actor"];

export interface Session {
  readonly isLoading: boolean;
  readonly isAuthenticated: boolean;
  readonly actor: Actor | null;
  readonly role: ErpRole | null;
  readonly can: (permission: Permission) => boolean;
  readonly refetch: () => void;
}

export function useSession(): Session {
  const query = trpc.auth.me.useQuery(undefined, { staleTime: 60_000, retry: false });
  const data = query.data;
  const actor = data !== undefined && data.authenticated ? data.actor : null;

  return {
    isLoading: query.isLoading,
    isAuthenticated: actor !== null,
    actor,
    role: actor?.role ?? null,
    can: (permission: Permission): boolean => can(actor?.role ?? null, permission),
    refetch: (): void => { void query.refetch(); },
  };
}
